import React, { useMemo } from 'react'
import LockIcon from '../../../../assets/icons/LockIcon'
import { ConversionCampaignOffer, ConversionCampaignOfferMilestone, OfferMeta } from '../../../../types/conversions'
import { formatAmount } from '../../../../utils/helpers'
import { Currency } from '../../../../types'

type MilestoneStatus = 'completed' | 'active' | 'pending'

const safePercent = (value: number) => {
    if (!Number.isFinite(value)) return 0
    return Math.max(0, Math.min(100, value))
}

const renderAsteriskBold = (text: string) => {
    const parts = text.split('*')
    if (parts.length === 1) return text

    return parts.map((part, idx) => {
        if (idx % 2 === 1) {
            return (
                <span className="offer-milestones__desc-strong" key={`b-${idx}`}>
                    {part}
                </span>
            )
        }
        return <React.Fragment key={`t-${idx}`}>{part}</React.Fragment>
    })
}

const getStatus = (m: ConversionCampaignOfferMilestone): MilestoneStatus => {
    const p = safePercent(Number(m?.progress?.percent ?? 0))
    if (p >= 100) return 'completed'
    if (p > 0) return 'active'
    return 'pending'
}

const getEarnExtraLabel = (m: ConversionCampaignOfferMilestone, currency: Currency) => {
    const title = `${m?.title ?? ''}`
    const percentMatch = title.match(/(\d+(?:\.\d+)?)\s*%/i)
    if (percentMatch?.[1]) return `${percentMatch[1]}%`
    const amount = Number(m?.bonus_value?.amount ?? 0)
    return formatAmount(amount, currency?.code, currency?.symbol, currency?.locale)
}

const OfferMilestones: React.FC<{ offer: ConversionCampaignOffer, meta: OfferMeta, currency: Currency }> = ({ offer, meta, currency }) => {

    const achievedAmount = useMemo(() => {
        return offer.milestone_visible ? (offer.type === 'creator_commission' ? Number(offer.optin?.total_commission_amount) : Number(offer.optin?.total_sale_amount)) : (offer.visibility_target_type === 'sale' ? Number(offer.optin?.total_sale_amount) : Number(offer.optin?.total_commission_amount))


    }, [offer.optin.total_commission_amount, offer.optin.total_sale_amount, offer.visibility_target_type, offer.type, offer.milestone_visible])

    const milestones = useMemo(() => {
        const raw = offer?.milestones ?? []
        const withStatus = raw.map((m) => ({ milestone: m, status: getStatus(m) }))

        const firstActiveIdx = withStatus.findIndex((x) => x.status === 'active')
        return withStatus.map((x, idx) => ({
            ...x,
            status: firstActiveIdx !== -1 && idx > firstActiveIdx && x.status === 'active' ? 'pending' : x.status
        }))
    }, [offer?.milestones])

    const activeIndex = useMemo(() => milestones.findIndex((m) => m.status === 'active'), [milestones])
    const timelineFill = useMemo(() => {
        if (activeIndex === -1) return '0px'
        const ACTIVE_TO_NEXT = 171
        const LOCKED_STEP = 113
        return `${ACTIVE_TO_NEXT + Math.max(0, activeIndex) * LOCKED_STEP}px`
    }, [activeIndex])

    return (
        <div className="offer-milestones">
            <div className="offer-milestones__timeline" style={{ ['--milestones-fill' as any]: timelineFill }}>
                <div className="offer-milestones__timeline-fill" />
            </div>

            <div className="offer-milestones__list">
                {milestones.map(({ milestone, status }, idx) => {
                    const targetAmount = Number(milestone.target)
                    const progressPercent = safePercent(Number(milestone?.progress?.percent ?? 0))
                    const bonusAmount = Number(milestone?.bonus_value?.amount ?? 0)
                    const earnExtraLabel = getEarnExtraLabel(milestone, currency)

                    return (
                        <div className={`offer-milestones__row is-${status}`} key={`${milestone.idx}-${idx}`}>
                            <div className="offer-milestones__icon">
                                {status === 'active' || status === 'completed' ? (
                                    <div className="offer-milestones__icon-active">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M5.33967 3.15472C6.07855 1.82924 6.44799 1.1665 7.00033 1.1665C7.55266 1.1665 7.9221 1.82924 8.66098 3.15472L8.85214 3.49764C9.0621 3.8743 9.16709 4.06263 9.33078 4.18689C9.49447 4.31115 9.69833 4.35728 10.1061 4.44953L10.4773 4.53352C11.9121 4.85816 12.6295 5.02048 12.8002 5.56935C12.9708 6.11821 12.4818 6.69013 11.5036 7.83396L11.2505 8.12988C10.9726 8.45493 10.8336 8.61744 10.7711 8.81851C10.7085 9.01957 10.7296 9.2364 10.7716 9.67008L10.8098 10.0649C10.9577 11.591 11.0317 12.3541 10.5848 12.6933C10.138 13.0325 9.46627 12.7232 8.12285 12.1047L7.77529 11.9447C7.39354 11.7689 7.20266 11.681 7.00033 11.681C6.79799 11.681 6.60712 11.7689 6.22536 11.9447L5.8778 12.1047C4.53439 12.7232 3.86268 13.0325 3.41583 12.6933C2.96898 12.3541 3.04292 11.591 3.19081 10.0649L3.22907 9.67008C3.27109 9.2364 3.2921 9.01957 3.22958 8.81851C3.16706 8.61744 3.02807 8.45493 2.75011 8.12988L2.49705 7.83396C1.51889 6.69013 1.02981 6.11821 1.20049 5.56935C1.37117 5.02048 2.08858 4.85816 3.52339 4.53352L3.89459 4.44953C4.30232 4.35728 4.50619 4.31115 4.66988 4.18689C4.83357 4.06263 4.93855 3.8743 5.14851 3.49764L5.33967 3.15472Z" fill="#22C55E" />
                                        </svg>
                                    </div>
                                ) : (
                                    <div className="offer-milestones__icon-locked">
                                        <LockIcon />
                                    </div>
                                )}
                            </div>

                            {status === 'active' || status === 'completed' ? (
                                <div className="offer-milestones__card app-card offer-milestones__card-active">
                                    <div className="offer-milestones__card-surface">
                                        <div className="offer-milestones__card-body">
                                            <div className="offer-milestones__title">{milestone.title}</div>
                                            <div className="offer-milestones__desc">
                                                {milestone.description ? (
                                                    renderAsteriskBold(milestone.description)
                                                ) : (
                                                    <>
                                                        Complete {offer.visibility_target_type} worth <span className="offer-milestones__desc-strong">{formatAmount(targetAmount, currency?.code, currency?.symbol, currency?.locale)}</span> to get a additional reward of{' '}
                                                        <span className="offer-milestones__desc-strong">{formatAmount(bonusAmount, currency?.code, currency?.symbol, currency?.locale)}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="offer-milestones__progress">
                                            <div className="my-progress__offer-bar">
                                                <div
                                                    className={`my-progress__offer-bar-fill is-${meta.color_class}`}
                                                    style={{ width: `${progressPercent}%` }}
                                                />
                                            </div>

                                            <div className="offer-milestones__progress-text">
                                                <span className={`offer-milestones__progress-current is-${meta.color_class}`}>{formatAmount(achievedAmount, currency?.code, currency?.symbol, currency?.locale)} </span>
                                                <span className="offer-milestones__progress-of">OF {formatAmount(targetAmount, currency?.code, currency?.symbol, currency?.locale)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`offer-milestones__bottom is-${meta.color_class}`}>
                                        {status === 'completed' ? (
                                            <>You&apos;ve earned {milestone.reward}</>
                                        ) : (
                                            <>Earn Extra {milestone.reward}</>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="offer-milestones__card app-card offer-milestones__card-locked">
                                    <div className="offer-milestones__card-surface">
                                        <div className="offer-milestones__card-body">
                                            <div className="offer-milestones__title">{milestone.title}</div>
                                            <div className="offer-milestones__desc">
                                                {milestone.description ? (
                                                    renderAsteriskBold(milestone.description)
                                                ) : (
                                                    <>
                                                        Complete {offer.visibility_target_type} worth <span className="offer-milestones__desc-strong">{formatAmount(targetAmount, currency?.code, currency?.symbol, currency?.locale)}</span> to get a additional reward of{' '}
                                                        <span className="offer-milestones__desc-strong">{formatAmount(bonusAmount, currency?.code, currency?.symbol, currency?.locale)}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="offer-milestones__locked-bonus">
                                        <span className="offer-milestones__locked-bonus-icon">
                                            <LockIcon />
                                        </span>
                                        <span className="offer-milestones__locked-bonus-text">{milestone.reward} Bonus</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default OfferMilestones